/**
 * Examine: a line or two about anything you can point at. Written from the game's own data, so
 * every item, creature, person and object has something to say, and new ones do too.
 */
import {
  CREATURES, GEAR, ITEMS, LEGENDS, NPCS, OBSTACLES, RECIPE_FOR, RESOURCES, SHOPS, SKILL_NAMES, STATIONS, SUPPLIES, TOOLS,
  creatureDef, gearValue, improvised, isCreatureKind, isGearId, isImprovised, isSupplyId, isToolId, parseVariant,
  type ItemId, type MapObject, type ResourceKind,
} from '@sunderfen/shared';

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const an = (s: string) => (/^[aeiou]/i.test(s) ? 'an' : 'a') + ' ' + s;
export const fmtCoins = (n: number) => (n >= 1e6 ? `${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1)}M` : n >= 1e4 ? `${Math.round(n / 1000)}k` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));

/** What one of this item is worth to a general trader. */
export function worthOf(id: string): number {
  if (id === 'coins') return 1;
  if (isGearId(id) && !isImprovised(id)) return gearValue(GEAR[id]);
  return (ITEMS as Record<string, { value?: number }>)[id]?.value ?? 0;
}

/** Examine text for an item. */
export function examineItem(id: string): string {
  if (id === 'coins') return 'Coins, stamped long after the End. Everyone still takes them.';
  const it = (ITEMS as Record<string, { name: string; cat: string; food?: number; heal?: number; desc?: string }>)[id];
  if (!it) return 'Something.';
  const v = parseVariant(id), name = it.name.replace(/ \((fine|superior|masterwork|worn|broken)\)$/i, '');
  const extra: string[] = [];
  if (v.grade) extra.push(`${cap(v.grade)} work`);
  if (v.wear === 'worn') extra.push('worn: mend it at an anvil before it breaks');
  if (v.wear === 'broken') extra.push('broken: it does nothing until mended');
  const tail = extra.length ? ` ${extra.join('; ')}.` : '';
  if (isGearId(id) && !isImprovised(id)) return `${GEAR[id].desc || `${an(name.toLowerCase())}.`}${tail}`;
  if (isToolId(id)) return `${TOOLS[id].desc}${tail}${isImprovised(id) ? ` In a pinch it's a weapon too (${improvised(id)!.acc} accuracy, ${improvised(id)!.str} strength).` : ''}`;
  if (isSupplyId(id as ItemId)) return SUPPLIES[id as keyof typeof SUPPLIES].desc;
  const lg = (LEGENDS as Record<string, { lore?: string }>)[id];
  if (lg?.lore) return lg.lore;
  if (it.desc) return it.desc;
  const made = RECIPE_FOR[id];
  switch (it.cat) {
    case 'log': return `${cap(name)}. Burn it, fletch it, or feed it to a furnace.`;
    case 'ore': return `${cap(name)}. Smelt it at a furnace${made ? '' : ''}.`;
    case 'bar': return `${an(name.toLowerCase())}. Hammer it into something at an anvil.`;
    case 'raw': return `${an(name.toLowerCase())}. Cook it before you eat it.`;
    case 'meat': return `${cap(name)}. Cook it first.`;
    case 'cooked': return `${cap(name)}. Heals ${it.heal ?? 0} hitpoints.`;
    case 'hide': return `${cap(name)}. Tanners and smiths pay for these.`;
    case 'valuable': return `${cap(name)}. Worth a good deal to the right buyer.`;
    case 'trophy': return `${cap(name)}. Something to be proud of.`;
    case 'fittings': return `${cap(name)}. Small metalwork that sells well.`;
    case 'junk': return `${cap(name)}. Junk, mostly. Someone might want it.`;
    case 'quest': return `${cap(name)}. Someone is waiting for this.`;
    case 'fletching': return `${cap(name)}. Fletching stock.`;
    case 'ammo': return `${cap(name)}. For a bow.`;
    case 'ingredient': return `${cap(name)}. Something for the pot.`;
    default: return made ? `${cap(name)}. ${SKILL_NAMES[made.skill]} makes these.` : `${an(name.toLowerCase())}.`;
  }
}

export function examineCreature(kind: string): string {
  if (!isCreatureKind(kind)) return 'Something alive.';
  const d = creatureDef(kind);
  return `${d.blurb}${d.aggressive ? ' It attacks on sight.' : ''}`;
}
export function examineNpc(kind: string, name: string): string {
  const d = (NPCS as Record<string, { title?: string } | undefined>)[kind], shop = SHOPS[kind];
  return `${name}${d?.title ? `, ${d.title.toLowerCase()}` : ''}.${shop ? ` ${shop.blurb ?? 'Runs a stall.'}` : ''}`;
}
export function examineObject(o: MapObject): string {
  const name = o.props?.name ? String(o.props.name) : '';
  if (o.type === 'resource' && o.kind in RESOURCES) {
    const r = RESOURCES[o.kind as ResourceKind], item = ITEMS[r.item]?.name.toLowerCase() ?? '';
    return `${cap(r.noun)}${name ? ` (${name})` : ''}. ${SKILL_NAMES[r.skill]} ${r.level}${item ? `: gives ${item}` : ''}.`;
  }
  if (o.type === 'station') { const st = (STATIONS as Record<string, { name: string; blurb: string } | undefined>)[o.kind]; return st ? `${name || st.name}. ${st.blurb}` : name || cap(o.kind); }
  if (o.type === 'obstacle') { const d = (OBSTACLES as Record<string, { name: string; desc?: string } | undefined>)[o.kind]; return d ? `${d.name}.${d.desc ? ` ${d.desc}` : ' Something in the way.'}` : 'In the way.'; }
  if (o.type === 'landmark') return `${name || cap(o.kind)}. A landmark of the old world.`;
  if (o.type === 'house') return 'Somebody lives here.';
  if (o.kind === 'stall') return 'A market stall.';
  return name || cap(o.kind.replace(/_/g, ' ')) + '.';
}
export { CREATURES };
